import { useState } from "react";
import { useHistory } from "react-router-dom";
import Sidebar from "@/pages/sidebar/sidebar";
import ClientMainHeader from "@/pages/header";
import WelcomeDialog from "./welcome-dialog/dialog";
import MobileViewDashboard from "@/pages/mobile-view/dashboard";
import { CustomSelector } from "@/pages/utils/styled-select";
import { chart_date_options, money_list } from "@/pages/demo";
import { convertThousandSeparator } from "@/scripts/functions";
import { MainWrapper } from "@/pages/styled";
import {
  ChartCard,
  ChartRadioLabel,
  DateOptionBtn,
  FeatureCard,
  FeatureCardBalance,
  FeatureCardLabel,
  InvoiceChartTitle,
  PaymentCardSection,
  RedirectBtnSection,
  SmallCard,
  FeatureCardBalanceDetail,
  StyledSelectedRadio,
  MainLabel,
  FeatureBalanceCard,
} from "./styled";
import { getWelcomeName } from "./utils";
import { InvoiceChart } from "./components";
import { useSelector } from "react-redux";
import { useInvoiceKpis, useProfileInfo } from "./hooks";
import { LimitedAccountWarning } from "@/components/limited-account-warning";
import { MerchantProcessingBanner } from "@/components/merchant-processing-banner";
import { useAuth } from "@/providers/auth";
import { useAccountBalance } from "@/hooks/account/use-account-balance";
import { useCheckDepositDrawer } from "@/hooks/invoice/use-check-deposit-drawer";
import { useTenancy } from "@/hooks/tenancy/use-tenancy";
import { CreateContactDrawer } from "@/components/contact/create-contact-drawer";

const Dashboard = () => {
  const [dateOption, setDateOption] = useState(30);
  const {
    invoiceChartData,
    totalAmount,
    paidAmount,
    unpaidAmount,
    pendingAmount,
    totalCount,
    paidCount,
    unpaidCount,
    dateForAmount,
    totalValueForAmount,
    paidValueForAmount,
    unpaidValueForAmount,
    dateForCount,
    totalValueForCount,
    paidValueForCount,
    unpaidValueForCount,
    pendingCount,
  } = useInvoiceKpis(dateOption);

  const { welcomeDialog, openWelcomeDialog } = useProfileInfo();
  const auth = useAuth();
  const { data: balance } = useAccountBalance();

  const { settingData } = useSelector((state) => state.account);

  const [shortCut, setShortCut] = useState("");
  const [type, setType] = useState({ label: "Invoices", value: "invoices" });
  const [kpiOption, setKpiOption] = useState({
    label: "Dollars",
    value: "dollars",
  });

  const [paymentOption, setPaymentOption] = useState("created");

  const [currentWidth, setCurrentWidth] = useState(0);

  const getBalanceFontSize = (text) => {
    const len = String(text).length;
    if (len <= 10) return "30px";
    if (len <= 13) return "24px";
    if (len <= 16) return "20px";
    return "16px";
  };

  const totalData = () => {
    if (paymentOption === "created") {
      if (kpiOption.value === "dollars") {
        return totalValueForAmount;
      } else {
        return totalValueForCount;
      }
    } else {
      return [];
    }
  };

  const paidData = () => {
    if (paymentOption === "paid") {
      if (kpiOption.value === "dollars") {
        return paidValueForAmount;
      } else {
        return paidValueForCount;
      }
    } else {
      return [];
    }
  };

  const unpaidData = () => {
    if (paymentOption === "unpaid") {
      if (kpiOption.value === "dollars") {
        return unpaidValueForAmount;
      } else {
        return unpaidValueForCount;
      }
    } else {
      return [];
    }
  };

  const chartOptions = {
    grid: { top: 8, right: 8, bottom: 24, left: 70 },
    xAxis: {
      type: "category",
      data: kpiOption.value === "dollars" ? dateForAmount : dateForCount,
    },
    yAxis: {
      type: "value",
      axisLabel: {
        formatter: kpiOption.value === "dollars" ? "$ {value}" : "{value}",
      },
    },
    series: [
      {
        data: totalData(),
        type: "line",
        showSymbol: false,
        lineStyle: { color: "#000000" },
      },
      {
        data: paidData(),
        type: "line",
        showSymbol: false,
        lineStyle: { color: "#939393" },
      },
      {
        data: unpaidData(),
        type: "line",
        showSymbol: false,
        lineStyle: { color: "#e1e1e1" },
      },
    ],
    tooltip: {
      trigger: "axis",
    },
  };

  return (
    <div>
      <WelcomeDialog dialog={welcomeDialog} setDialog={openWelcomeDialog} />
      <Sidebar currentWidth={currentWidth} setCurrentWidth={setCurrentWidth} />
      {currentWidth < 768 ? (
        <MobileViewDashboard
          bondBalance={balance}
          businessName={getWelcomeName(auth.user?.user_profile)}
          kpiOption={kpiOption}
          setKpiOption={setKpiOption}
          dateOption={dateOption}
          setDateOption={setDateOption}
          chartOptions={chartOptions}
          invoiceChartData={invoiceChartData}
        />
      ) : (
        <>
          <LimitedAccountWarning />
        <MerchantProcessingBanner />
          <MainWrapper>
            <ClientMainHeader
              title="Dashboard"
              subTitle={`Welcome ${getWelcomeName(auth.user?.user_profile)}`}
              shortCut={shortCut}
              setShortCut={setShortCut}
            />

            <LinkBankStepper />

            <div>
              <MainLabel>Summary</MainLabel>
              <div className="flex">
                <CustomSelector
                  value={kpiOption}
                  setValue={setKpiOption}
                  selectList={money_list}
                  width={120}
                  menuWidth={240}
                  mBottom={16}
                  menuLeft
                />
              </div>
              <PaymentCardSection>
                <FeatureCard style={{ width: 238, height: 112 }}>
                  <div>
                    <FeatureCardLabel>Created</FeatureCardLabel>
                    <FeatureCardBalance style={{ fontSize: getBalanceFontSize(kpiOption.value === "dollars" ? `$${convertThousandSeparator(totalAmount)}` : totalCount) }}>
                      {kpiOption.value === "dollars"
                        ? `$${convertThousandSeparator(totalAmount)}`
                        : totalCount}
                    </FeatureCardBalance>
                  </div>
                </FeatureCard>
                <FeatureCard style={{ width: 238, height: 112 }}>
                  <div>
                    <FeatureCardLabel>Paid + Processing</FeatureCardLabel>
                    <FeatureCardBalance style={{ fontSize: getBalanceFontSize(kpiOption.value === "dollars" ? `$${convertThousandSeparator(paidAmount + pendingAmount)}` : paidCount) }}>
                      {kpiOption.value === "dollars"
                        ? `$${convertThousandSeparator(
                            paidAmount + pendingAmount
                          )}`
                        : paidCount}
                      <FeatureCardBalanceDetail
                        kpiOption={kpiOption.value === "dollars"}
                        paid={
                          kpiOption.value === "dollars" ? paidAmount : paidCount
                        }
                        processing={
                          kpiOption.value === "dollars"
                            ? pendingAmount
                            : pendingCount
                        }
                      />
                    </FeatureCardBalance>
                  </div>
                </FeatureCard>
                <FeatureCard style={{ width: 238, height: 112 }}>
                  <div>
                    <FeatureCardLabel>Unpaid</FeatureCardLabel>
                    <FeatureCardBalance style={{ fontSize: getBalanceFontSize(kpiOption.value === "dollars" ? `$${convertThousandSeparator(unpaidAmount)}` : unpaidCount) }}>
                      {kpiOption.value === "dollars"
                        ? `$${convertThousandSeparator(unpaidAmount)}`
                        : unpaidCount}
                    </FeatureCardBalance>
                  </div>
                </FeatureCard>
              </PaymentCardSection>
              <ChartCard>
                <InvoiceChartTitle>Invoices</InvoiceChartTitle>
                <div className="flex justify-between align-items-center pb-25">
                  <div className="flex">
                    {chart_date_options.map((option) => (
                      <div key={option.value}>
                        {dateOption === option.value ? (
                          <DateOptionBtn style={{ background: "#e1e1e1" }}>
                            {option.label}
                          </DateOptionBtn>
                        ) : (
                          <DateOptionBtn
                            onClick={() => setDateOption(option.value)}
                          >
                            {option.label}
                          </DateOptionBtn>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="flex">
                    {/* {payment_options.map((option, index) =>
                    paymentOption === option.value ? (
                      <div
                        key={index}
                        className="flex align-items-center mr-20 cursor-pointer"
                      >
                        <StyledSelectedRadio />
                        <ChartRadioLabel>{option.label}</ChartRadioLabel>
                      </div>
                    ) : (
                      <div
                        key={index}
                        className="flex align-items-center mr-20 cursor-pointer"
                        onClick={() => setPaymentOption(option.value)}
                      >
                        <StyledRadio />
                        <ChartRadioLabel>{option.label}</ChartRadioLabel>
                      </div>
                    )
                  )} */}
                    <div
                      key={"button-paid"}
                      className="flex align-items-center mr-20 cursor-pointer"
                    >
                      <StyledSelectedRadio
                        primaryColor={settingData?.primary_color}
                        status="paid"
                      />
                      <ChartRadioLabel>Paid</ChartRadioLabel>
                    </div>
                    <div
                      key={"button-pendind-and-paid"}
                      className="flex align-items-center mr-20 cursor-pointer"
                    >
                      <StyledSelectedRadio
                        primaryColor={settingData?.primary_color}
                        status="processingandpaid"
                      />
                      <ChartRadioLabel>Processing + Paid</ChartRadioLabel>
                    </div>
                    <div
                      key={"button-unpaid"}
                      className="flex align-items-center mr-20 cursor-pointer"
                    >
                      <StyledSelectedRadio
                        primaryColor={settingData?.primary_color}
                        status="unpaid"
                      />
                      <ChartRadioLabel>Unpaid</ChartRadioLabel>
                    </div>
                  </div>
                </div>
                <InvoiceChart
                  primaryColor={settingData?.primary_color}
                  invoiceData={invoiceChartData}
                />
              </ChartCard>
            </div>
          </MainWrapper>
        </>
      )}
    </div>
  );
};

export function LinkBankStepper() {
  const history = useHistory();
  const { data: tenancy } = useTenancy();
  const { user } = useAuth();
  const { handleOpen } = useCheckDepositDrawer();
  const [createContactOpen, setCreateContactOpen] = useState(false);

  return (
    <div className="flex pb-38">
      <FeatureBalanceCard />
      <RedirectBtnSection>
        <SmallCard onClick={() => history.push("/invoices/new")}>
          Create an invoice
        </SmallCard>
        <SmallCard
          onClick={() => {
            setCreateContactOpen(true);
          }}
        >
          Add a contact
        </SmallCard>
        {tenancy?.feature_flags?.mrdc && (
          <SmallCard onClick={() => handleOpen("true")}>
            Deposit check
          </SmallCard>
        )}
        <SmallCard onClick={() => history.push("/move-money")}>
          Move money{" "}
        </SmallCard>
      </RedirectBtnSection>
      <CreateContactDrawer
        open={createContactOpen}
        handleClose={() => setCreateContactOpen(false)}
      />
    </div>
  );
}

export default Dashboard;
